<template>
    <div id="role-cell-wrapper" class="user-role-cell">
      <v-select
        class='role-field'
        :items='roleItems'
        v-model='selectedRoles'
        outlined
        dense
        multiple
        :disabled="isDisabled"
        :menu-props="{ bottom: true, offsetY: true }"
        @change="handleRoleChange"
      >
        <template v-slot:selection="{ item, index }">
          <span v-if="index === 0" class="role-display-text">{{ getDisplayText() }}</span>
        </template>
      </v-select>
    </div>
</template>

<script>
import {mapGetters} from 'vuex';
import {
  LOAD_ALL_TENANT_USERS,
  ADMIN_CHANGE_USER_ROLE,
} from '@/store/user/actionTypes';
import {CONFIGURATION_ACTION_TYPES} from '@/constants/configuration';
import {tenantRoles, userRoles} from '@/constants/roles';

export default {
  name: 'RoleTableCell',
  props: {
    item: {
      type: Object,
      required: true,
    },
    column: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      selectedRoles: [],
      isDisabled: false,
    };
  },
  created() {
    this.initializeRoles();
  },
  computed: {
    ...mapGetters(['tenantUsers']),
    baseRolesList() {
      return [
        tenantRoles.TENANT_ADMIN,
        tenantRoles.TENANT_USER,
        tenantRoles.TENANT_UNPRIVILEGED_USER,
      ];
    },
    billingRolesList() {
      return [
        tenantRoles.BILLING_ADMIN,
        tenantRoles.BILLING,
      ];
    },
    displayMap() {
      return userRoles;
    },
    displayText() {
      const baseRole = this.selectedRoles.find(r => this.baseRolesList.includes(r));
      const billingRole = this.selectedRoles.find(r => this.billingRolesList.includes(r));

      const baseDisplay = this.displayMap[baseRole] || userRoles.TenantUnprivilegedUser;

      if (!billingRole) {
        return baseDisplay;
      }

      return `${baseDisplay}, ${this.displayMap[billingRole]}`;
    },
    roleItems() {
      return [
        {header: 'Base Role'},
        {text: userRoles.TenantAdmin, value: tenantRoles.TENANT_ADMIN},
        {text: userRoles.TenantUser, value: tenantRoles.TENANT_USER},
        {text: userRoles.TenantUnprivilegedUser, value: tenantRoles.TENANT_UNPRIVILEGED_USER},
        {divider: true},
        {header: 'Billing Role (Optional)'},
        {text: userRoles.BillingAdmin, value: tenantRoles.BILLING_ADMIN},
        {text: userRoles.Billing, value: tenantRoles.BILLING},
      ];
    },
  },
  watch: {
    'item.role': {
      handler() {
        this.initializeRoles();
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    initializeRoles() {
      const roles = Array.isArray(this.item.role) ? this.item.role : [];
      const baseRole = roles.find(r => this.baseRolesList.includes(r)) || tenantRoles.TENANT_UNPRIVILEGED_USER;
      const billingRole = roles.find(r => this.billingRolesList.includes(r));
      this.selectedRoles = billingRole ? [baseRole, billingRole] : [baseRole];
    },
    getDisplayText() {
      return this.displayText;
    },
    handleRoleChange(newRoles) {
      const selectedBaseRoles = newRoles.filter(r => this.baseRolesList.includes(r));
      const selectedBillingRoles = newRoles.filter(r => this.billingRolesList.includes(r));

      let needsUpdate = false;

      if (selectedBaseRoles.length > 1) {
        const lastSelected = selectedBaseRoles[selectedBaseRoles.length - 1];
        this.selectedRoles = newRoles.filter(r => !this.baseRolesList.includes(r) || r === lastSelected);
        needsUpdate = true;
      }

      if (selectedBillingRoles.length > 1) {
        const lastSelected = selectedBillingRoles[selectedBillingRoles.length - 1];
        this.selectedRoles = newRoles.filter(r => !this.billingRolesList.includes(r) || r === lastSelected);
        needsUpdate = true;
      }

      if (selectedBaseRoles.length === 0) {
        this.selectedRoles = [...newRoles.filter(r => !this.baseRolesList.includes(r)), tenantRoles.TENANT_UNPRIVILEGED_USER];
        needsUpdate = true;
      }

      // Billing roles are optional - no need to add 'None' if empty

      if (needsUpdate) {
        this.$nextTick(() => {
          this.updateRole();
        });
      } else {
        this.updateRole();
      }
    },
    async updateRole() {
      this.isDisabled = true;
      await this.$emit('disableDeleteButton', this.item.id);

      const roles = {
        oldRole: this.item.role,
        newRole: this.selectedRoles,
      };

      try {
        await this.$store.dispatch(ADMIN_CHANGE_USER_ROLE, {
          id: this.item.id,
          roles,
          successMessageType: CONFIGURATION_ACTION_TYPES.CHANGE_ROLE,
        });
      } catch (e) {
        await this.$emit('handleChangeUserRoleError', e.message);
      }

      await this.$store.dispatch(LOAD_ALL_TENANT_USERS);
      await this.$emit('disableDeleteButton', this.item.id);
      this.isDisabled = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.user-role-cell {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

#role-cell-wrapper .role-field.v-text-field {
  margin-top: 0;
}

.role-field {
  flex: 0 0 auto;
  width: 200px;
  min-width: 200px;
  max-width: 200px;
}

.role-field ::v-deep .v-select__selections {
  flex-wrap: nowrap;
}

.role-display-text {
  display: inline-block;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.role-field ::v-deep .v-input__control {
  min-height: 32px;
}

.role-field ::v-deep .v-input__slot {
  cursor: pointer;
  min-height: 32px;
}

.role-field ::v-deep .v-select__selections {
  cursor: pointer;
}

.role-field ::v-deep .v-list-item__content {
  align-items: center;
}

.role-field ::v-deep .v-list-item__title {
  line-height: 1.5;
}

.role-field ::v-deep .v-simple-checkbox {
  align-self: center;
}

// Dark mode support
[data-theme="dark"] {
  .role-field {
    ::v-deep .v-select__selections,
    ::v-deep .v-select__selection,
    ::v-deep .role-display-text,
    ::v-deep input {
      color: var(--text-primary) !important;
    }

    ::v-deep .v-input__slot {
      background-color: var(--bg-tertiary);
    }
  }

  .role-display-text {
    color: var(--text-primary);
  }
}
</style>
